<template>
  <div class="input-group" :class="{'disable': isDisable, 'error': hasValidationError()}">
    <input
      :id="name"
      :name="name"
      :type="typeOfInput"
      :placeholder="placeholder"
      :disabled="isDisable"
      v-model="inputModel"
      :autocomplete="autocomplete"
      class="form-control"
      :class="{'input-passive': isPassive}"
      @keydown="$emit('keydown', $event)"
    >
    <ButtonElement
                   v-if="withButton"
                   @clickOnButton="clickOnButton"
                   :is-input-button="true"
                   class-type="button">
      <slot>
        <LucideIcon :icon="showPasswordFlag ? 'EyeOff' : 'Eye'"/>
      </slot>
    </ButtonElement>
  </div>

  <div v-if="hasValidationError()" class="validation-error">
    <LucideIcon icon="Info"/>
    <span
      v-for="(error, index) of validator.$errors"
      :key="index"
    >
      {{ error.$message }}
    </span>
  </div>
</template>

<script lang="ts">

import { defineComponent } from 'vue'
import { AbstractInputComponent } from '@shared/components/abstracts'
import { CommonNs } from '@shared/types'
import ButtonElement from '@shared/components/elements/button/button.element.vue'

export default defineComponent({
  name: 'PasswordElement',
  extends: AbstractInputComponent,
  components: {
    ButtonElement
  },
  emits: ['update:modelValue', 'keydown'],
  props: {
    modelValue: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: CommonNs.InputTypeEnums.PASSWORD
    },
    placeholder: {
      type: String,
      default: 'Password'
    },
    withButton: {
      type: Boolean,
      default: true
    },
    isPassive: {
      type: Boolean,
      default: false
    },
    validator: {
      type: Object,
      default: null
    },
    autocomplete: {
      type: String,
      default: 'off',
      enum: ['on', 'off']
    }
  },
  data () {
    return {
      typeOfInput: CommonNs.InputTypeEnums.PASSWORD,
      showPasswordFlag: false
    }
  },
  computed: {
    inputModel: {
      get (): string {
        return this.modelValue
      },
      set (value: string): void {
        this.$emit('update:modelValue', value)
      }
    }
  },
  methods: {
    hasValidationError (): boolean {
      return this.validator && this.validator.$errors.length
    },

    clickOnButton (): void {
      this.showPasswordFlag = !this.showPasswordFlag
      this.changeInputType()
    },
    changeInputType (): void {
      this.typeOfInput = this.showPasswordFlag
        ? CommonNs.InputTypeEnums.TEXT
        : CommonNs.InputTypeEnums.PASSWORD
    }
  },
  mounted (): void {
    this.changeInputType()
  }
})

</script>

<style lang="scss" scoped>

.input-group {
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: $br-sm;
  background-color: var(--form-bg);
  border: 1px solid var(--form-border);
  box-shadow: none !important;
  color: var(--text-dark);

  &:hover {
    background-color: var(--form-bg-hover);
    border: 1px solid var(--form-border-hover);
    box-shadow: var(--form-shadow-outline) !important;
  }

  &:has(input:focus) {
    background-color: var(--form-bg-focus);
    border: 1px solid var(--form-border-focus);
    box-shadow: var(--form-shadow-outline) !important;

    .tag {
      background-color: var(--tag-form-bg-hover);
      border: 1px solid var(--tag-form-border-hover);
    }
  }

  &.disable {
    background-color: var(--form-bg);
    border: 1px solid var(--form-border);

    input {
      color: var(--text-neutral);
      box-shadow: 0 !important;
    }

    &:hover {
      background-color: var(--form-bg-hover);
      border: 1px solid var(--form-border-hover);
      box-shadow: var(--form-shadow-outline) !important;
    }
  }

  .btn {
    border-radius: $br-sm !important;
    margin-right: 10px;
  }

  &.error {
    box-shadow: 0px 0px 4px 0px var(--red-100) !important;
    background-color: var(--light-100) !important;
    border-color: var(--light-100) !important;
  }

  input,
  .input-group-text {
    background: transparent;
    border: 0;
  }

  .input-group-text {
    color: var(--text-neutral);
  }

  input {
    height: 36px;
    border-radius: $br-sm;
    font-size: var(--fs-14);
    color: var(--text-dark);
    cursor: pointer;

    &::-webkit-input-placeholder { /* Edge */
      color: var(--text-neutral);
    }

    &:-ms-input-placeholder { /* Internet Explorer 10-11 */
      color: var(--text-neutral);
    }

    &::placeholder {
      color: var(--text-neutral);
    }
  }

  .icon-side {
    align-items: center;
    height: 100%;
    padding: 0 15px 0 0px;
    color: var(--text-neutral);
  }

  .icon {
    width: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    color: var(--text-neutral);
  }

}

</style>
